import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { alpha, Box, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { AnnouncementAdminDetailFormShape } from '@/apis/service/announcement';
import Request from '@/apis/config';
import type { AnnouncementExistingMedia } from '@/pages/announcement/components/CreateAnnouncementForm';
import {
  AnnouncementMediaLightboxDialog,
  AnnouncementMediaThumb,
  THUMB_BOX_SX,
  guessMimeFromUrl,
  isProtectedAnnouncementFileUrl,
  isRemoteMediaVideo,
  remoteMediaLabel,
  resolveProtectedMediaThumb,
  type ProtectedPreviewEntry,
} from '@/pages/announcement/announcementProtectedMedia';

const PURPOSE_I18N: Record<string, string> = {
  GAME_UPDATE: 'announcement.purpose_game_update',
  REGULAR_MAINTENANCE: 'announcement.purpose_regular_maintenance',
  URGENT_NOTICE: 'announcement.purpose_urgent_notice',
  DIRECT_INPUT: 'announcement.purpose_direct_input',
};

function purposeLabel(purpose: string | undefined, t: (key: string) => string): string {
  if (!purpose?.trim()) return '—';
  const trimmed = purpose.trim();
  const key = PURPOSE_I18N[trimmed] ?? (trimmed.startsWith('announcement.purpose_') ? trimmed : null);
  return key ? t(key) : trimmed;
}

type Props = {
  detail: AnnouncementAdminDetailFormShape;
};

export default function AnnouncementNotificationBody({ detail }: Props) {
  const { t } = useTranslation();
  const [protectedPreview, setProtectedPreview] = useState<Record<string, ProtectedPreviewEntry>>({});
  const [remoteLightbox, setRemoteLightbox] = useState<{ url: string; isVideo: boolean; label: string } | null>(null);

  const mediaKey = useMemo(
    () => (detail.media ?? []).map((m) => `${m.url ?? ''}:${m.type ?? ''}`).join('\u0001'),
    [detail.media],
  );

  const ensureProtectedObjectUrl = useCallback(
    async (url: string, isVideo: boolean) => {
      if (!isProtectedAnnouncementFileUrl(url)) return url;
      const existing = protectedPreview[url];
      if (existing?.objectUrl) return existing.objectUrl;
      if (existing?.loading) return url;

      setProtectedPreview((prev) => ({ ...prev, [url]: { ...prev[url], loading: true, error: undefined } }));
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches CreateAnnouncementForm / Request typings
        const buf: any = await Request.downloadFile(url);
        const arrayBuffer = buf instanceof ArrayBuffer ? buf : buf?.data ?? buf;
        const blob = new Blob([arrayBuffer], { type: guessMimeFromUrl(url, isVideo) });
        const objUrl = URL.createObjectURL(blob);
        setProtectedPreview((prev) => ({ ...prev, [url]: { objectUrl: objUrl, loading: false } }));
        return objUrl;
      } catch (e: unknown) {
        const err = e as { message?: string };
        setProtectedPreview((prev) => ({
          ...prev,
          [url]: { loading: false, error: err?.message ?? 'Preview failed' },
        }));
        return url;
      }
    },
    [protectedPreview],
  );

  useEffect(() => {
    setProtectedPreview((prev) => {
      Object.values(prev).forEach((v) => {
        if (v?.objectUrl) URL.revokeObjectURL(v.objectUrl);
      });
      return {};
    });
  }, [mediaKey]);

  useEffect(() => {
    return () => {
      Object.values(protectedPreview).forEach((v) => {
        if (v?.objectUrl) URL.revokeObjectURL(v.objectUrl);
      });
    };
  }, [protectedPreview]);

  useEffect(() => {
    const items = detail.media ?? [];
    items.forEach((m) => {
      const url = m?.url ?? '';
      if (!url) return;
      if (!isProtectedAnnouncementFileUrl(url)) return;
      if (isRemoteMediaVideo(m)) return;
      if (protectedPreview[url]?.objectUrl || protectedPreview[url]?.loading) return;
      void ensureProtectedObjectUrl(url, false);
    });
  }, [detail.media, ensureProtectedObjectUrl, protectedPreview]);

  const rows: { label: string; value: ReactNode }[] = [
    { label: t('announcement.vendor'), value: detail.vendor?.trim() || '—' },
    { label: t('announcement.purpose'), value: purposeLabel(detail.purpose, t) },
    { label: t('announcement.period'), value: detail.period?.trim() || '—' },
    {
      label: t('announcement.content'),
      value: (
        <Typography component="div" variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {detail.content?.trim() || '—'}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <Stack spacing={2} sx={{ py: 0.5 }}>
        {rows.map(({ label, value }) => (
          <Box key={label}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
              {label}
            </Typography>
            {typeof value === 'string' ? <Typography variant="body2">{value}</Typography> : value}
          </Box>
        ))}
        {detail.media?.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
              {t('announcement.file')}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1.5}>
              {detail.media.map((m: AnnouncementExistingMedia, idx: number) => {
                const url = m.url ?? '';
                const video = isRemoteMediaVideo(m);
                const label = remoteMediaLabel(url);
                const protectedEntry = isProtectedAnnouncementFileUrl(url) ? protectedPreview[url] : undefined;
                const thumb = resolveProtectedMediaThumb(url, m, protectedEntry);
                return (
                  <Paper
                    key={`${url}-${idx}`}
                    elevation={0}
                    sx={{
                      width: 160,
                      maxWidth: '100%',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: (theme) => alpha(theme.palette.text.primary, 0.2),
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: (theme) =>
                          theme.palette.mode === 'dark'
                            ? '0 8px 24px rgba(0,0,0,0.45)'
                            : '0 10px 28px rgba(0,0,0,0.12)',
                      },
                    }}>
                    <Box
                      sx={{ ...THUMB_BOX_SX, cursor: 'pointer' }}
                      onClick={async () => {
                        const u = await ensureProtectedObjectUrl(url, video);
                        setRemoteLightbox({ url: u, isVideo: video, label });
                      }}>
                      <AnnouncementMediaThumb thumb={thumb} />
                    </Box>
                    <Tooltip title={label} placement="top">
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{
                          display: 'block',
                          px: 1.25,
                          py: 1,
                          bgcolor: 'background.paper',
                          borderTop: '1px solid',
                          borderColor: 'divider',
                        }}>
                        {label}
                      </Typography>
                    </Tooltip>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        )}
      </Stack>

      <AnnouncementMediaLightboxDialog remoteLightbox={remoteLightbox} onClose={() => setRemoteLightbox(null)} />
    </>
  );
}
